'use client'; // remove with zendesk feature flag

/* eslint-disable max-lines */
import React from 'react';

import { ExternalLink } from '../../../components/Clickables/ExternalLink/ExternalLink';
import { Heading } from '../../../components/Heading/Heading';
import { InternalLink } from '../../../components/Clickables/InternalLink/InternalLink';
import { FeatureFlag } from '../../../components/FeatureFlag/FeatureFlag';

const TermsOfService = () => {
  return (
    <div className="text-justify leading-6">
      <Heading as="h1" className="mb-4">
        Terms of service
      </Heading>
      <p>Last updated: June 3rd, 2025</p>
      <p>
        Previous version:{' '}
        <InternalLink to="/policies/terms-of-service/3.0">March 5th, 2024</InternalLink>
      </p>
      <p>
        These Terms of Service (the “Terms”) apply to your use of the Fishbrain mobile application,
        our website at http://www.fishbrain.com (collectively with our apps and websites, the
        “Sites”), our platform to sell fishing-related goods and services (the “Fishbrain Shop”),
        and our other applications, software, features, tools, and services related to any of the
        foregoing (collectively, the “Services”). The Fishbrain Sites are made provided by Fishbrain
        AB, Reg. No 556796-5370, a company duly incorporated and organized under the laws of Sweden,
        having its registered address at Sveav&auml;gen 15, 111 57 Stockholm, Sweden [(the
        “Fishbrain Sites Entity”). The Fishbrain Shop is provided by Fishbrain Inc., a Delaware,
        U.S., corporation having a place of business at 3500 South DuPont Highway, Suite JM-101,
        Dover, DE, 19901 (the “Fishbrain Shop Entity”). The Fishbrain Sites Entity and the Fishbrain
        Shop Entity referred to collectively as “Fishbrain.” As used herein, ‘Fishbrain’, ‘we,’ and
        ‘us’ refers to Fishbrain AB and/or Fishbrain Inc., as applicable. You are referred to herein
        as “you.” Users of the Services are referred to herein as “Users.” The Fishbrain Sites and
        Services related thereto are provided pursuant to the laws of Sweden and the Fishbrain Shop
        and Services related thereto are provided pursuant to the laws of the state of Delaware,
        U.S.
      </p>
      <p>
        These Terms create a legal agreement directly between you and Fishbrain and explain the
        rules governing use of the Services. You agree to comply with, the Terms, our privacy policy
        located at http://www.fishbrain.com/privacy , all updates to the foregoing, and any
        additional terms we may make available to you on the Services, and all applicable local,
        state, national, and international laws, rules, and regulations.
      </p>
      <p>
        <strong>
          BY ACCESSING, USING OR UPLOADING OR DOWNLOADING ANY INFORMATION OR MATERIALS TO OR FROM
          THE SERVICES, BY CREATING AN ACCOUNT, OR BY CLICKING “SIGN UP” OR ANY SIMILAR MECHANISM,
          YOU ARE AGREEING TO THESE TERMS. IF YOU DO NOT AGREE TO THESE TERMS, DO NOT ACCESS OR USE
          THE SERVICES.
        </strong>{' '}
        This agreement was written in English. To the extent a translated version of the Terms
        conflict with the English version, the English version controls.{' '}
        <strong>
          NOTICE REGARDING DISPUTE RESOLUTION: THESE TERMS CONTAIN PROVISIONS THAT GOVERN HOW
          DISPUTES BETWEEN YOU AND FISHBRAIN ARE RESOLVED, INCLUDING AN AGREEMENT TO ARBITRATE,
          WHICH WILL, UNLESS YOU RESIDE IN THE EUROPEAN UNION, WITH LIMITED EXCEPTION, REQUIRE YOU
          TO SUBMIT CLAIMS YOU HAVE AGAINST US TO BINDING AND FINAL ARBITRATION AND LIMIT YOU TO
          CLAIMS AGAINST FISHBRAIN ON AN INDIVIDUAL BASIS, UNLESS YOU OPT-OUT IN ACCORDANCE WITH THE
          INSTRUCTIONS BELOW.
        </strong>
      </p>
      <Heading variant="sm" className="mb-4">
        1. Registration and accounts
      </Heading>
      <p>
        The Services are intended solely for persons who are 13 years old or such higher age
        required in your country to use the Services. If you are under the legal age to form a
        binding contract in your jurisdiction, you represent that your parent or legal guardian has
        reviewed and agreed to these Terms on your behalf.
      </p>
      <p>
        To use the Services, you must register. You agree to: (a) provide true, accurate, current
        and complete information about yourself (collectively, “Registration Data”) and (b) maintain
        and promptly update the User Data. You agree that Fishbrain may use your User Data to
        provide Services that you access or use and as otherwise set forth in these Terms. If you
        provide any User Data that is inaccurate or not current, or Fishbrain has reasonable grounds
        to suspect that such User Data is inaccurate or not current, Fishbrain has the right to
        suspend or terminate your account and refuse current or future use of the Services. In
        consideration of your use of the Services, you represent that you are not a person barred
        from receiving services under the laws of any applicable jurisdiction.
      </p>
      <p>
        You are solely responsible for maintaining the confidentiality of the password associated
        with your account and for restricting access to your password, and for restricting access to
        your computer and your mobile device while logged into the Services. You accept
        responsibility for all activities that occur under your account or from your computer and
        mobile devices. We endeavor to use reasonable security measures to protect against
        unauthorized access to your account. We cannot, however, guarantee absolute security of your
        account, your Content (as defined below), or the personal information you provide, and we
        cannot promise that our security measures will prevent third-parties from illegally
        accessing the Services or its contents. You agree to immediately notify Fishbrain of any
        unauthorized use of your account or password, or any other breach of security, and confirm
        that you understand all risks of unauthorized access to User Data and any other information
        or content you provide to Fishbrain.
      </p>
      <p>
        You acknowledge that you are fully responsible for obtaining all relevant fishing permits or
        other authorizations needed in order to fish at a location which may be presented to you in
        our Services. You agree not to use our Service in connection with any unpermitted,
        unauthorized, illegal, or dangerous (to you or others) fishing or other activities.
      </p>
      <Heading variant="sm" className="mb-4">
        2. Content
      </Heading>
      <p>
        Fishbrain holds the qualities of respect for others and originality of creation in high
        regard. When you use or interact with our Services and/or with other Users of the Services,
        you may elect to send, display, distribute, use, submit, upload, communicate, transmit, or
        otherwise make available to us and/or to other Users of the Services content, including but
        not limited to information, data, photographs, video, text, sound, software, graphics,
        messages, posts, tags information on your geographical position ratings, reviews, comments,
        opinions, and other content (collectively, &quot;Content&quot;) whether publicly posted,
        privately transmitted, or submitted through a third party API (e.g. a photograph submitted
        via Instagram).
      </p>
      <p>
        For all Content you use, send, upload, communicate, transmit, or otherwise make available in
        connection with the Services, you grant to Fishbrain and our affiliates an unlimited,
        non-exclusive, irrevocable, worldwide, perpetual, royalty-free, fully sublicensable and
        fully transferable right to use, copy, reproduce, change, modify, adapt, edit, distribute,
        translate, transfer, host, store, display, create derivative works from, make publicly
        available and/or perform such Content in relation to our Services throughout the world in
        any media, including but without limitation for purposes of operating, developing,
        providing, promoting, and improving the Services and researching and developing new ones. To
        the fullest extent permitted under applicable law, you waive your moral rights (or
        &apos;droit moral&apos;) to and/or in the Content. You acknowledge and agree that we may
        share Content with our affiliates, partners, and companies that we cooperate with, as well
        as third party services such as Facebook and Instagram.
      </p>
      <p>
        When you appear in, create, upload, post, or send Content via the Services, you also grant
        Fishbrain, our affiliates, and our business partners and licensees the unrestricted,
        worldwide, perpetual right and license to use your name, likeness, and voice, and
        information about your activities and actions you have taken, including your use of third
        party products, services, or devices, in ads, offers, and other commercial contexts on the
        Services and otherwise in connection with commercial or sponsored content without
        compensation to you. This means, among other things, that you will not be entitled to any
        compensation from Fishbrain, our affiliates, or our business partners if your name,
        likeness, or voice is conveyed through the Services, either on the Fishbrain application or
        on one of our business partner’s platforms.
      </p>
      <p>
        You agree that the Content you send, upload, communicate, transmit, distribute, display,
        use, submit, or otherwise make available, and your use of the Services:
      </p>
      <ul className="list-disc pl-4">
        <li className="text-sm">
          <p>is true and accurate and not false or misleading;</p>
        </li>
        <li className="text-sm">
          <p>
            is not threatening, disparaging, hate speech, an incitement to violence, defamatory,
            pornographic, racially or ethnically offensive, graphic violence, discriminatory,
            obscene, insulting, harassing, intimidating, bullying, slanderous or otherwise illegal,
            injurious, objectionable, or inappropriate;
          </p>
        </li>
        <li className="text-sm">
          <p>
            does not infringe or otherwise violate the intellectual property (including copyright),
            publicity, privacy, or other rights of any third party;
          </p>
        </li>
        <li className="text-sm">
          <p>
            is not information that you are not legally entitled to distribute (such as insider
            information or confidential information);
          </p>
        </li>
        <li className="text-sm">
          <p>
            does not contain any unsolicited or unauthorized advertising, commercial solicitation,
            political campaigning, promotional material, &apos;junk mail&apos;, &apos;spam&apos;,
            &apos;chain letters&apos;, mass mailing, &apos;pyramid schemes&apos; or any other form
            of solicitation; and
          </p>
        </li>
        <li className="text-sm">
          <p>
            does not contain software virus or any other technology that may harm the Services, or
            the interests or property of our Services or the other Users of the Services.
          </p>
        </li>
      </ul>
      <p>
        If your Content violates these Terms or is otherwise objectionable, you may bear legal
        responsibility for that Content. As between you and Fishbrain, any Content will be
        non-confidential and non-proprietary and we will not be liable for any use or disclosure of
        Content. You acknowledge and agree that your relationship with Fishbrain is not a
        confidential, fiduciary, or other type of special relationship, and that your decision to
        submit any Content does not place Fishbrain in a position that is any different from the
        position held by members of the general public, including with regard to your Content. Use
        of your Content is governed by Fishbrain’s Privacy Policy located at
        https://fishbrain.com/privacy.
      </p>
      <p>
        Fishbrain appreciates the opportunity to be notified of any objectionable or unauthorized
        use of Content, and Users, rights holders and licensees are invited to inform Fishbrain of
        any potential violations by sending an email to us at support@fishbrain.com.
      </p>
      <p>
        Fishbrain hereby reserves the right in its absolute discretion, at any time and for any
        reason, including to provide and develop the Services or if we think your Content violates
        these Terms or is otherwise objectionable, to screen, monitor, refuse, remove or edit any
        Content or activity from the Services, but is not required to do so. Fishbrain is not
        responsible, and assumes no liability, for any third party content or Content provided by
        you, nor information contained therein or made available or otherwise used in connection
        with the Services, and is not responsible for the deletion or loss of any content or the
        failure of any content to comply with these Terms.
      </p>
      <p>
        Read more in the{' '}
        <FeatureFlag
          featureFlag="zendesk"
          newComponent={
            <ExternalLink href="https://fishbrainhelp.zendesk.com/hc/en-us/articles/22539035568540-What-are-Fishbrain-s-Guidelines">
              Fishbrain Guidelines
            </ExternalLink>
          }
          oldComponent={
            <ExternalLink href="https://fishbrain.helpshift.com/hc/en/3-fishbrain---social-fishing-forecast-app/faq/153-what-are-fishbrain-s-guidelines/">
              Fishbrain Guidelines
            </ExternalLink>
          }
        />
        .
      </p>
      <Heading variant="sm" className="mb-4">
        3. Use of the services
      </Heading>
      <Heading variant="2xs" as="h3" className="mb-4">
        User conduct
      </Heading>
      <p>
        Certain rules must be complied with in order to maintain the integrity, legality, and safety
        of the Services and to promote the User experience. By using the Services you agree that:
      </p>
      <ul className="list-disc pl-4">
        <li className="text-sm">
          <p>
            You are thirteen (13) years of age or above; no one under 13 may create an account or
            use the Services;
          </p>
        </li>
        <li className="text-sm">
          <p>You can form a binding contract with Fishbrain;</p>
        </li>
        <li className="text-sm">
          <p>
            You are not a person who is barred from using the Services under the laws of the United
            States or any other applicable jurisdiction—meaning that you do not appear on the U.S.
            Treasury Department’s list of Specially Designated Nationals or face any other similar
            prohibition and are not the subject of U.S. sanctions or of sanctions consistent with
            U.S. law imposed by the governments of the country where you are using Services.
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will comply with all U.S. or other export and re-export restrictions that may apply
            to goods, software (including our software), technology, and services.
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will use the Services and Content only as expressly permitted in these Terms; you
            will not use the Services or Content for any purpose that is illegal or prohibited by
            these Terms or enable or assist anyone else to do so, nor will you encourage or promote
            any activity that violates these Terms;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will use the Services and Content for your own private, personal purposes only and
            not for any commercial use other than purchasing products through the Service, such as
            marketing or sale of goods or Services, or for any business or other entity;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will only send, upload, communicate, transmit, distribute, display or otherwise make
            available Content that you own or otherwise have all necessary rights in and are
            permitted to make available in the Services;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will not use another person or entity&apos;s name or e-mail address or identity when
            you use our Services or mislead as to the origin of Content, nor will you solicit login
            credentials from another User or use or attempt to use another User’s account, username,
            or password;
          </p>
        </li>
        <li className="text-sm">
          <p>
            If you create a User account, you will only create one account per platform, provide
            your full name and a valid e-mail address through which we will be able to contact you
            as well as any other step required in order to sign up for an account; you agree to keep
            your login details to your User account secure and confidential, which includes but is
            not limited to not disclosing your login details to anyone else or allowing someone else
            to use your login details or account; you will not create another account if we disable
            your account; you will not buy, sell, rent or lease access to your account; and you
            accept full responsibility for the activities carried out by the use of your account or
            credentials;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will not use any robot, spider, crawler, scraper, or other automated means or
            interface to access the Services, gather or extract data, or collect any personal or
            other information (such as User name and e-mail address) regarding users of our
            Services;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will not decompile, disassemble or reverse engineer the Services or attempt to
            extract the source code of software that is part of the Services, or circumvent,
            deactivate or otherwise interfere with any technological measure or security-related
            feature of the Services;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will not copy, modify, distribute, sell, archive, download, upload, syndicate,
            broadcast, perform, display, make available, or lease any part of our Services or
            content;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will not use or develop any third-party applications that interact with the Services
            or other users’ content or information without our written consent;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will not use the Services in a way that could interfere with, disrupt, negatively
            affect, or inhibit other users from fully enjoying the Services, or that could damage,
            disable, overburden, or impair the functioning of the Services;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will not upload viruses or other malicious code or otherwise compromise the security
            of the Services;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will not attempt to circumvent any content-filtering techniques we employ, or
            attempt to access areas or features of the Services that you are not authorized to
            access;
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will not probe, scan, or test the vulnerability of our Services or any system or
            network and
          </p>
        </li>
        <li className="text-sm">
          <p>
            You will not remove or amend any copyright or other proprietary notices, or use
            branding, logos, designs, photographs, videos or any other materials used in our
            Services unless you own or otherwise have the express right to use such content for such
            purpose.
          </p>
        </li>
      </ul>
      <Heading variant="2xs" as="h3" className="mb-4">
        Interactions with Users
      </Heading>
      <p>
        The Services function, among other things, as a venue to connect users in a virtual
        information place. As a neutral facilitator, Fishbrain is not directly involved in the
        actual interactions between users of the Services. As a result, Fishbrain has no control
        over the truth, accuracy, quality, legality, or safety of postings made by users of the
        Services. Fishbrain shall have no responsibility to confirm the identity of users. Fishbrain
        shall also have no responsibility to confirm or verify the qualifications, background, or
        abilities of users of the Services. You shall at all time exercise common sense and good
        judgment when dealing with any User of the Services.
      </p>
      <p>
        If you elect to use our features to inform your contacts about the Services or share your
        information with others, Fishbrain may require you to provide contact information. You
        represent that you are authorized to provide any third party contact information that you
        provide to Fishbrain, that you are authorized to use such information to contact (including
        for Fishbrain to contact on your behalf) the third party, and that Fishbrain may process it
        pursuant to the https://fishbrain.com/privacy.
      </p>
      <Heading variant="sm" className="mb-4">
        4. Privacy
      </Heading>
      <p>
        When you sign up for an account you provide personal details to us that may be deemed as
        personal information under the applicable legislation. By using our Services, you agree to
        our collection and use and sharing of personal and other data in accordance with our Privacy
        Policy, which is published at: https://fishbrain.com/privacy.
      </p>
      <p>
        In addition, if you enable the Services to interact with any third party (such as social
        network sites), you give us permission to use information which that third party shares with
        us. You are advised to read third party terms carefully to see what information they might
        share with us.
      </p>
      <Heading variant="sm" className="mb-4">
        5. Cookies
      </Heading>
      <p>
        When you visit or interact with our Services, Fishbrain uses and our authorized services
        providers may use cookies to store certain types of information for each time you visit or
        interact with our Services. By accepting these Terms you agree to Fishbrain using cookies in
        accordance with our Cookie Policy, which is published at:
        https://fishbrain.com/cookie-policy.
      </p>
      <Heading variant="sm" className="mb-4">
        6. Termination and cancellation
      </Heading>
      <p>You may terminate your account and use of the Services at any time.</p>
      <p>
        You agree that Fishbrain may, without prior notice, immediately terminate your account
        and/or access to the Services for causes that include but are not limited to, the following:
        (a) breaches or violations of the Terms or other applicable agreements, policies or
        guidelines, (b) requests by law enforcement or other government agencies, (c) a request by
        you (self-initiated account deletions), (d) discontinuance or material modification to the
        Services (or any portion thereof), (e) unexpected technical or security issues or problems,
        (f) extended periods of inactivity, and/or (g) nonpayment of any fees owed by you in
        connection with the Services. Termination of your account may include (x) removal of access
        to all offerings within the Services, (y) deletion of your information, files, and Content
        associated with your account, and (z) barring of further use of the Services. All
        terminations for cause shall be made in Fishbrain’s sole discretion and that Fishbrain shall
        not be liable to you or any third party for any termination of your account or access to the
        Services.
      </p>
      <p>
        All license grants set forth in these Terms and expressly identified as “perpetual” will,
        along with the following Sections, survive termination for any reason of your account, these
        Terms, and/or of the Services: Sections 4 (Privacy), 9.2(c) (Recalls), 10 (Third Party
        Services), 13 (Disclaimer of Warranties and Liability), 15 (Indemnity), 16 (Choice of Law
        and Forum), 17 (Dispute Resolution and Forum – Fishbrain Shop), and 18 (General).
      </p>
      <Heading variant="sm" className="mb-4">
        7. Site policies, modifications, and severability
      </Heading>
      <p>
        Fishbrain may change the Services at any time, such as by adding or removing features or
        discontinuing the Services. We may also suspend or stop the Services altogether. We may take
        any of these actions at any time, and when we do, we may not provide you with any advance
        notice.
      </p>
      <p>
        Please review our other policies, including but not limited to our Privacy Policy and Cookie
        Policy, as well as our Fishbrain help page posted on our site. These policies also govern
        your use of Services. We reserve the right to make changes to our site, policies, and these
        Terms at any time without notice, effective upon posting or as otherwise required by
        applicable law. You are advised to regularly review the latest version which is published at
        https://fishbrain.com/terms-of-service. If you do not wish to accept such Terms, you can
        terminate your account. Any continued use by you of the Services following the notification
        of modified Terms shall constitute acceptance by you of such Terms. If any of these
        conditions shall be deemed invalid, void, or for any reason unenforceable, that condition
        shall be deemed severable and shall not affect the validity and enforceability of any
        remaining condition.
      </p>
      <Heading variant="sm" className="mb-4">
        8. Premium subscription
      </Heading>
      <p>
        You may subscribe to Fishbrain&apos;s premium service. If you are between 13 and 18 years of
        age your legal guardian must consent thereto and purchase the subscription for you and you
        may use the Services only with the involvement of a parent or guardian. We may change our
        subscription fees from time to time by posting the changes on the Fishbrain site. If you
        continue to use the Services after the new fees apply you accept and agree to pay the new
        fees.
      </p>
      <Heading variant="sm" className="mb-4">
        9. Fishbrain shop
      </Heading>
      <p>
        The Fishbrain Shop offers a wide array of fishing and related gear for purchase. The
        Fishbrain Shop is provided by Fishbrain, Inc. and is available only to U.S. Users of the
        Services. You must have your own Fishbrain account in order to use the Fishbrain Shop, and
        you may be required to be logged in to the account and have a valid payment method
        associated with it.
      </p>
      <Heading variant="2xs" as="h3" className="mb-4">
        Purchase/Order Processing, Fulfillment, and Shipping
      </Heading>
      <p>
        (a) Order Process. You may place orders to purchase items (“Product(s)”) in the Fishbrain
        Shop (“Order(s)”) from one of the third party sellers (“Seller(s)”) offering Products there.
      </p>
      <p>
        (b) Order Fulfillment. Sellers are solely responsible for, and bear all liability for, the
        fulfillment of each Order, including but not limited to packaging and shipping of Products,
        and customer service.
      </p>
      <p>
        (c) Shipping Options. Shipping options for all Products sold through the Fishbrain Shop will
        be made available to you by Seller at checkout. Products cannot be shipped outside the
        United States or in any region where the sale of such product violates any policy of the
        Seller or any or applicable law.
      </p>
      <Heading variant="2xs" as="h3" className="mb-4">
        Cancellations, Returns, Refunds, and Recalls
      </Heading>
      <p>
        (a) Cancellations and Returns. Returns for Products sold on the Fishbrain Shop that are
        eligible for return (e.g., not final sale) must be returned within 30 days of receipt of
        shipment in order to receive a full refund. Products must be returned in the original carton
        with original packaging, accessories, and instructions.
      </p>
      <p>
        (b) Refunds. If Seller determines you are due a cash refund, the refund will be made via
        your original payment method (e.g., credit or debit card).
      </p>
      <p>
        (c) Recalls. Fishbrain will have no responsibility or liability for any recalls of Products
        sold through the Fishbrain Shop. Seller is solely responsible for any non-conformity or
        defect in, or any public or private recall of, Seller’s Products.
      </p>
      <Heading variant="2xs" as="h3" className="mb-4">
        Product Descriptions
      </Heading>
      <p>
        Fishbrain attempts to be as accurate as possible. However, product descriptions are provided
        by sellers, and Fishbrain does not warrant that product descriptions or other content of any
        Service is accurate, complete, reliable, current, or error-free. If a product offered on the
        Fishbrain Shop is not as described, your sole remedy is to return it in unused condition per
        the Seller’s terms.
      </p>
      <Heading variant="2xs" as="h3" className="mb-4">
        Pricing
      </Heading>
      <p>
        With respect to items sold by Fishbrain, we cannot confirm the price of an item until you
        order. Despite our best efforts, a small number of the items on the Fishbrain Shop may be
        mispriced. If the correct price of an item sold on the Fishbrain Shop is higher than our
        stated price, we will, at our discretion, either contact you for instructions before
        shipping or cancel your order and notify you of such cancellation.
      </p>
      <Heading variant="sm" className="mb-4">
        10. Third party services
      </Heading>
      <p>
        Fishbrain may include links to, or otherwise make available through the Services, products,
        services, features, and/or functionality operated by a third party. Third Party Services
        made available on the Services are made and offered directly by the applicable third party.
        Your interaction with, or participation in promotions of, third parties found on or through
        the Services, and any other terms, are solely between you and such third party and are
        subject to such third party’s terms and conditions.
      </p>
      <p>
        You are advised to read third party terms, including terms of use/service and privacy
        policy, carefully as they constitute an agreement between you and the third party. You
        acknowledge and agree that Fishbrain is not responsible or liable for a third party’s terms,
        products, content or actions, including their failure to comply with their terms of
        use/service or privacy policy.
      </p>
      <p>
        YOU AGREE THAT FISHBRAIN SHALL NOT BE LIABLE FOR ANY LOSS OR DAMAGES OF ANY KIND INCURRED BY
        YOU AS THE RESULT OF ANY OF YOUR DEALINGS WITH THIRD PARTY SERVICE PROVIDERS OR ADVERTISERS
        AVAILABLE THROUGH THE SERVICES.
      </p>
      <p>
        Fishbrain or third parties may provide links to other internet sites or resources through
        the Services. Fishbrain does not endorse and is not responsible or liable for any content,
        information, advertising, products or other materials on or available from such sites or
        resources. You acknowledge and agree that Fishbrain is not responsible for the availability
        of such external sites or resources.
      </p>
      <p>
        <span>Fishbrain may embed YouTube videos through </span>
        <span>
          <ExternalLink href="https://developers.google.com/youtube/terms/developer-policies#definition-youtube-api-services">
            YouTube API Services
          </ExternalLink>
        </span>
        <span> and in such cases this integration is governed by the </span>
        <span>
          <ExternalLink href="https://www.youtube.com/t/terms">
            YouTube terms of service
          </ExternalLink>
        </span>{' '}
        and{' '}
        <ExternalLink href="http://www.google.com/policies/privacy">
          Google Privacy Policy
        </ExternalLink>
        <span>.</span>
      </p>
      <Heading variant="sm" className="mb-4">
        11. Intellectual property
      </Heading>
      <p>
        You acknowledge and agree that the Services, any necessary software used in connection with
        the Services, any data based on content on the Services, and any content available or made
        available on the Services contain proprietary and confidential information that is protected
        by applicable intellectual property and other laws. Except as expressly permitted by
        applicable law or authorized by Fishbrain or applicable third party service providers or
        advertisers, you agree not to modify, rent, lease, loan, sell, distribute or create
        derivative works based on the Services, the software or Content available on the Services
        (other than Content that you may submit), in whole or in part.
      </p>
      <p>
        Fishbrain grants you a personal, revocable, non-transferable, non-sublicensable,
        non-exclusive right and license to access and use the Services; -provided that you do not
        (and do not allow any third party to) copy, modify, create a derivative work from, reverse
        engineer, reverse assemble or otherwise attempt to discover any source code, sell, assign,
        sublicense, grant a security interest in or otherwise transfer any right in the Services,
        except where permitted by law.] You agree not to access the Services by any means other than
        through the interfaces that are provided by Fishbrain.
      </p>
      <p>
        The term “FISHBRAIN,” the Fishbrain logo and other Fishbrain logos and product and service
        names are the exclusive trademarks of, and are owned by, Fishbrain, Inc., and you may not
        use or display such trademarks in any manner without Fishbrain’s prior written permission.
        Any third party trademarks or service marks displayed on the Services are the property of
        their respective owners. Fishbrain reserves all rights not expressly granted hereunder.
      </p>
      <p>
        Fishbrain honors the requirements set forth in the Digital Millennium Copyright Act. We take
        reasonable steps to expeditiously remove from our Services any infringing material that we
        become aware of. And if Fishbrain becomes aware that one of its Users has repeatedly
        infringed copyrights, we will take reasonable steps within our power to terminate the User’s
        account.
      </p>
      <p>
        We make it easy for you to report suspected copyright infringement. If you believe that
        anything on the Services infringes a copyright that you own or control, please file a notice
        with our designated agent by emailing support@fishbrain.com. If you file a notice with our
        Copyright Agent, it must comply with the requirements set forth at{' '}
        <ExternalLink href="https://www.law.cornell.edu/uscode/text/17/512">
          17 U.S.C. § 512(c)(3)
        </ExternalLink>
        . That means the notice must:
      </p>
      <ul className="list-disc pl-4">
        <li className="text-sm">
          <p>
            contain the physical or electronic signature of a person authorized to act on behalf of
            the copyright owner.
          </p>
        </li>
        <li className="text-sm">
          <p>identify the copyrighted work claimed to have been infringed.</p>
        </li>
        <li className="text-sm">
          <p>
            identify the material that is claimed to be infringing or to be the subject of
            infringing activity and that is to be removed, or access to which is to be disabled, and
            information reasonably sufficient to let us locate the material.
          </p>
        </li>
        <li className="text-sm">
          <p>
            provide your contact information, including your address, telephone number, and an email
            address.
          </p>
        </li>
        <li className="text-sm">
          <p>
            provide a personal statement that you have a good-faith belief that the use of the
            material in the manner complained of is not authorized by the copyright owner, its
            agent, or the law.
          </p>
        </li>
        <li className="text-sm">
          <p>
            provide a statement that the information in the notification is accurate and, under
            penalty of perjury, that you are authorized to act on behalf of the copyright owner.
          </p>
        </li>
      </ul>
      <Heading variant="sm" className="mb-4">
        12. Your feedback
      </Heading>
      <p>
        We welcome your comments, feedback, suggestions, and other communications regarding the
        Services and the information and services we make available through the Services
        (collectively, “Feedback”). If you provide Feedback, you hereby grant to Fishbrain a
        worldwide, non-exclusive, transferable, assignable, sub- licensable, perpetual, irrevocable,
        royalty-free license to copy, distribute, create derivative works of, publicly display and
        perform and otherwise exploit such Feedback and to use, make, have made, sell, offer for
        sale, import and export products and services based on such Feedback. For this reason, we
        ask that you not send Fishbrain any Feedback that you do not wish to license to us as set
        forth above.
      </p>
      <Heading variant="sm" className="mb-4">
        13. Disclaimer of warranties and liability
      </Heading>
      <p>
        THE INFORMATION, SOFTWARE, PRODUCTS, SERVICES AND CONTENT AVAILABLE THROUGH THE SERVICES ARE
        PROVIDED TO YOU “AS IS” AND WITHOUT WARRANTY. FISHBRAIN AND ITS SUBSIDIARIES, AFFILIATES,
        OFFICERS, EMPLOYEES, AGENTS, PARTNERS AND LICENSORS HEREBY DISCLAIM ALL WARRANTIES WITH
        REGARD TO SUCH INFORMATION, SOFTWARE, PRODUCTS, SERVICES AND CONTENT, INCLUDING, WITHOUT
        LIMITATION, ALL IMPLIED WARRANTIES AND CONDITIONS OF MERCHANTABILITY, FITNESS FOR A
        PARTICULAR PURPOSE, TITLE AND NONINFRINGEMENT. FISHBRAIN AND ITS SUBSIDIARIES, AFFILIATES,
        OFFICERS, EMPLOYEES, AGENTS, PARTNERS AND LICENSORS MAKE NO WARRANTY THAT: (a) THE SERVICE
        WILL MEET YOUR REQUIREMENTS; (b) YOUR CONTENT WILL BE AVAILABLE OR THE SERVICE WILL BE
        UNINTERRUPTED, TIMELY, SECURE OR ERROR-FREE; (c) THE RESULTS THAT MAY BE OBTAINED FROM THE
        USE OF THE SERVICE WILL BE ACCURATE OR RELIABLE; (d) THE QUALITY OF ANY PRODUCTS, SERVICES,
        INFORMATION OR OTHER MATERIAL PURCHASED OR OBTAINED BY YOU THROUGH THE SERVICE WILL MEET
        YOUR EXPECTATIONS; (e) SELLERS AND OTHER THIRD PARTY PROVIDERS WILL COMPLY WITH THEIR
        RESPECTIVE COMMITMENTS, TERMS OR POLICIES, AND (e) ANY ERRORS IN THE SERVICES WILL BE
        CORRECTED.
      </p>
      <p>
        YOU EXPRESSLY AGREE TO RELEASE FISHBRAIN, ITS SUBSIDIARIES, AFFILIATES, OFFICERS, AGENTS,
        REPRESENTATIVES, EMPLOYEES, PARTNERS AND LICENSORS (THE “RELEASED PARTIES”) FROM ANY AND ALL
        LIABILITY IN CONNECTION WITH YOUR FISHING ACTIVITIES AND/OR USE OF THE FISHBRAIN WEBSITES,
        MOBILE APPLICATIONS, CONTENT, SERVICES OR PRODUCTS, INCLUDING PRODUCTS PURCHASED ON THE SHOP
        AND PROMISE NOT TO SUE THE RELEASED PARTIES FOR ANY CLAIMS, ACTIONS, INJURIES, DAMAGES, OR
        LOSSES ASSOCIATED WITH SUCH USE. YOU ALSO AGREE THAT IN NO EVENT SHALL THE RELEASED PARTIES
        BE LIABLE TO YOU OR ANY THIRD PARTY FOR ANY DIRECT, INDIRECT, PUNITIVE, INCIDENTAL, SPECIAL
        OR CONSEQUENTIAL DAMAGES ARISING OUT OF OR IN ANY WAY CONNECTED WITH (a) YOUR USE OR MISUSE
        OF THE SERVICES, (b) YOUR USE OR MISUSE OF EQUIPMENT OR PROGRAMS CREATED OR LICENSED BY
        FISHBRAIN WHILE ENGAGED IN FISHING ACTIVITIES, (c) YOUR DEALINGS WITH THIRD-PARTY SERVICE
        PROVIDERS OR ADVERTISERS AVAILABLE THROUGH THE SERVICES, (d) ANY DELAY OR INABILITY TO USE
        THE SERVICES EXPERIENCED BY YOU, OR (e) ANY INFORMATION, SOFTWARE, PRODUCTS, SERVICES OR
        CONTENT OBTAINED THROUGH THE SERVICES, WHETHER BASED ON CONTRACT, TORT, STRICT LIABILITY OR
        OTHERWISE, EVEN IF FISHBRAIN HAS BEEN ADVISED OF THE POSSIBILITY OF DAMAGES.
      </p>
      <p>
        BECAUSE SOME STATES/JURISDICTIONS DO NOT ALLOW SOME OR ALL OF THE EXCLUSIONS OR LIMITATIONS
        OF LIABILITY ABOVE, SUCH AS EXCLUSIONS FOR CONSEQUENTIAL OR INCIDENTAL DAMAGES, SOME OF THE
        ABOVE LIMITATIONS OR EXCLUSIONS MAY NOT APPLY TO YOU. IN ENTERING INTO THIS RELEASE, YOU
        ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTAND, AND HEREBY EXPRESSLY WAIVE THE BENEFITS OF,
        SECTION 1542 OF THE CIVIL CODE OF CALIFORNIA, (AND ANY SIMILAR LAW OF ANY STATE, COUNTRY OR
        TERRITORY), WHICH PROVIDES AS FOLLOWS: “A GENERAL RELEASE DOES NOT EXTEND TO CLAIMS WHICH
        THE CREDITOR DOES NOT KNOW OR SUSPECT TO EXIST IN HIS FAVOR AT THE TIME OF EXECUTING THE
        RELEASE, WHICH IF KNOWN BY HIM MUST HAVE MATERIALLY AFFECTED HIS SETTLEMENT WITH THE
        DEBTOR.”
      </p>
      <p>
        FISHBRAIN DOES NOT ENDORSE YOUR CONTENT OR ANY OTHER USER’S CONTENT AND SPECIFICALLY
        DISCLAIMS ANY RESPONSIBILITY OR LIABILITY TO ANY PERSON OR ENTITY FOR ANY LOSS, DAMAGE
        (WHETHER ACTUAL, CONSEQUENTIAL, INDIRECT, PUNITIVE OR OTHERWISE), INJURY, CLAIM, LIABILITY
        OR OTHER CAUSE OF ANY KIND OR CHARACTER BASED UPON OR RESULTING FROM ANY CONTENT ON THE
        SERVICES. THE COMPANY’S MAXIMUM AGGREGATE LIABILITY TO YOU PURSUANT TO THESE TERMS SHALL NOT
        EXCEED THE GREATER OF EITHER (a) FIFTY DOLLARS ($50) OR (b) THE AMOUNT OF FEES PAID BY YOU
        TO FISHBRAIN IN THE 12-MONTHS PRIOR TO DATE THE CLAIM.
      </p>
      <p>
        IF YOU ARE A CONSUMER RESIDING IN THE EUROPEAN UNION, YOU HAVE LEGAL REMEDIES IF WE CAUSE
        YOU LOSS BY OUR BREACH OR NON-PERFORMANCE OF OUR LEGAL OBLIGATIONS, OR BY OUR NEGLIGENCE,
        AND THESE TERMS DO NOT AFFECT THOSE STATUTORY REMEDIES.
      </p>

      <Heading variant="sm" className="mb-4">
        14. Notice for California users
      </Heading>
      <p>
        Under California Civil Code Section 1789.3, California Services Users are entitled to the
        following specific consumer rights notice: The Complaint Assistance Unit of the Division of
        Consumer Services of the California Department of Consumer Affairs may be contacted in
        writing at 1625 N. Market Blvd., Suite N 112, Sacramento, California 95834, or by telephone
        at (800) 952-5210.
      </p>
      <Heading variant="sm" className="mb-4">
        15. Indemnity
      </Heading>
      <p>
        You agree to indemnify and hold Fishbrain and its subsidiaries, affiliates, officers,
        agents, representatives, employees, partners and licensors harmless from any claim or
        demand, including reasonable attorneys’ fees, made by any third party due to or arising out
        of Content you submit, post, transmit or otherwise seek to make available through the
        Services, your use of the Services, your use of any products obtained on the Shop, your
        fishing activities which generate the Content you post or seek to post on the Services, your
        connection to the Services, your violation of the Terms, your violation of any data
        protection or privacy or other laws, or your violation of any rights of another person or
        entity. Your rights with respect to Fishbrain are not modified by the foregoing
        indemnification if the laws of your country of residence, applicable as a result of your use
        of our Services, do not permit it.
      </p>
      <Heading variant="sm" className="mb-4">
        16. Choise of law and forum
      </Heading>
      <p>
        Any action related to the Terms, Content, the Services, and/or your relationship with the
        Fishbrain, other than with respect to the Fishbrain Shop, shall be governed by, and
        construed and interpreted in accordance with, the laws of the Sweden without regard to its
        conflict of laws principles, and the parties irrevocably consent to bring any such action in
        and consent to the exclusive jurisdiction of the courts of Sweden. Any action related to the
        Fishbrain Shop will be governed by, and construed and interpreted in accordance with, the
        laws of the State of Delaware, U.S., and the parties irrevocably consent to dispute
        resolution per the terms of Section 17 below (Dispute Resolution) for any claims arising out
        of or relating to the Fishbrain Shop, which arbitration will take place in Wilmington,
        Delaware, U.S. THE UNITED NATIONS CONVENTIONS ON CONTRACTS FOR THE INTERNATIONAL SALE OF
        GOODS AND THE UNIFORM COMPUTER INFORMATION TRANSACTION ACT AS CURRENTLY ENACTED BY ANY
        JURISDICTION OR AS MAY BE CODIFIED OR AMENDED FROM TIME TO TIME BY ANY JURISDICTION DO NOT
        AND SHALL NOT APPLY IN ANY ACTION RELATED HERETO. and If any party hereto brings any suit or
        action against another for relief, declaratory or otherwise, arising out of these Terms, the
        prevailing party will have and recover against the other party, in addition to all court
        costs and disbursements, such sum as the court may adjudge to be reasonable attorneys’ fees.
      </p>
      <p>
        Notwithstanding the foregoing, if you are a User based in the European Union, your local
        laws in your European Union Member State may allow you to take legal action against
        Fishbrain in your Member State and to invoke certain local laws against Fishbrain.
      </p>
      <Heading variant="sm" className="mb-4">
        17. Dispute resolution - Fishbrain Shop
      </Heading>
      <Heading variant="2xs" as="h3" className="mb-4">
        Arbitration
      </Heading>
      <p>
        The parties shall use their best efforts to settle any dispute, claim, question, or
        disagreement directly through good-faith negotiations, which shall be a precondition to
        either party initiating a lawsuit or arbitration. Except for disputes relating to the
        Fishbrain’s intellectual property (such as trademarks, trade dress, domain names, trade
        secrets, copyrights, and patents) or if you opt out of this agreement to arbitrate, all
        claims arising out of or relating to the terms of this Agreement related to the Fishbrain
        Shop Services and/or your use of the Fishbrain Shop shall be finally settled by binding
        arbitration administered by JAMS in accordance with its provisions and procedures for
        consumer-related disputes, excluding any rules or procedures governing or permitting class
        actions. The arbitrator, and not any court or agency, shall have exclusive authority to
        resolve all disputes arising out of or relating to this Agreement, including, but not
        limited to, any claim that all or any part of this Agreement is void or voidable. The
        arbitrator shall be empowered to grant whatever relief would be available in a court;
        provided, however, that the arbitrator will not have authority to award damages, remedies,
        or awards that conflict with this Agreement. The arbitrator’s award shall be binding on the
        parties and may be entered as a judgment in any court of competent jurisdiction. To the
        extent the filing fee for the arbitration exceeds the cost of filing a lawsuit, Fishbrain
        will pay the additional cost. You and Fishbrain hereby expressly waive trial by jury. You
        also agree not to participate in claims brought in a private attorney general or
        representative capacity, or consolidated claims involving another person’s account, if
        Fishbrain is a party to the proceeding. This dispute resolution provision will be governed
        by the Federal Arbitration Act. This provision does not apply to users who live in the
        European Union.
      </p>
      <p>
        The parties understand that, absent this mandatory provision, they would have the right to
        sue in court and have a jury trial. They further understand that, in some instances, the
        costs of arbitration could exceed the costs of litigation and that the right to discovery
        may be more limited in arbitration than in court.
      </p>
      <Heading variant="2xs" as="h3" className="mb-4">
        Class-Action Waiver
      </Heading>
      <p>
        The parties further agree that any arbitration shall be conducted in their individual
        capacities only, and not as a class action or other representative action. If any court or
        arbitrator determines that the class-action waiver set forth in this section is void or
        unenforceable for any reason, or that an arbitration can proceed on a class basis, then the
        arbitration provision set forth above shall be deemed null and void in its entirety and the
        parties shall be deemed to have not agreed to arbitrate disputes.
      </p>
      <Heading variant="2xs" as="h3" className="mb-4">
        Exception—Litigation of Small Claims Court Claims
      </Heading>
      <p>
        Notwithstanding the parties’ decision to resolve all disputes through arbitration, either
        party may also seek relief in a U.S. small claims court for disputes or claims within the
        scope of that court’s jurisdiction.
      </p>
      <Heading variant="2xs" as="h3" className="mb-4">
        Thirty-Day Right to Opt Out
      </Heading>
      <p>
        You have the right to opt out and not be bound by the arbitration- and class-action-waiver
        provisions set forth in this section by sending written notice of your decision to opt out
        to the following address: legal@fishbrain.com. The notice must be sent within 30 days of
        registering to use the Services; otherwise, you shall be bound to arbitrate disputes in
        accordance with these Terms. If you opt out of these arbitration provisions, Fishbrain also
        will not be bound by them. In addition, if you elect to opt out of these arbitration
        provisions, Fishbrain may terminate your use of the Services.
      </p>
      <Heading variant="2xs" as="h3" className="mb-4">
        Time Limitation on Claims
      </Heading>
      <p>
        Except to the extent prohibited by applicable law, You agree that any claim you may have
        arising out of or related to your relationship with Fishbrain and these Terms must be filed
        within one year after such claim arose; otherwise, your claim is permanently barred. This
        provision does not apply if you are based in the European Union in which case time
        limitations shall be determined in accordance with governing law for EU users mentioned
        below.
      </p>
      <Heading variant="sm" className="mb-4">
        18. General
      </Heading>
      <p>
        You agree that no joint venture, partnership, joint controllership, employment or agency
        relationship exists between you and Fishbrain as a result of the Terms or your use of the
        Services. The Terms constitute the entire agreement between you and Fishbrain with respect
        to your use of the Services. The failure of Fishbrain to exercise or enforce any right or
        provision of the Terms shall not constitute a waiver of such right or provision. If any
        provision of the Terms is found by a court of competent jurisdiction to be invalid, the
        parties nevertheless agree that the court should endeavor to give effect to the parties’
        intentions as reflected in the provision, and the other provisions of the Terms remain in
        full force and effect. You may not assign, delegate or otherwise transfer your account or
        your obligations under these Terms without the prior written consent of Fishbrain.
      </p>
      <p>
        Fishbrain has the right, in its sole discretion, to transfer or assign all or any part of
        its rights under these Terms and will have the right to delegate or use third party
        contractors to fulfill its duties and obligations under these Terms and in connection with
        the Services. Fishbrain’s notice to you via email, regular mail or notices, posts, or links
        on the Services shall constitute acceptable notice to you under the Terms. A printed version
        of the Terms and of any notice given in electronic form shall be admissible in judicial or
        administrative proceedings based upon or relating to the Terms to the same extent and
        subject to the same conditions as other business documents and records originally generated
        and maintained in printed form. Section titles and headings in the Terms are for convenience
        only and have no legal or contractual effect. Any rights not expressly granted herein are
        reserved.
      </p>
      <p>
        Fishbrain and its third party service providers may make improvements and/or changes in the
        Services, products, Services, mobile applications, features, programs, and prices described
        at any time and for any reason in its sole discretion. The mobile application may download
        and install upgrades, updates and additional features in order to improve, enhance, and
        further develop the Services. Fishbrain reserves the right at any time to modify or
        discontinue, temporarily or permanently, the Services or any portion thereof with or without
        notice. You agree that Fishbrain shall not be liable to you or to any third party for any
        modification, suspension or discontinuance of the Services.
      </p>
      <p>
        <strong>SUPPORT AND QUESTIONS</strong>. We will respond to any questions regarding the
        Services and these Terms via support@fishbrain.com. We generally respond to support requests
        within 10-14 days after the request is placed.
      </p>
    </div>
  );
};

// Needs default to auto load correct file

export default TermsOfService;
